import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, Briefcase, KanbanSquare, LogOut,
  TrendingUp, DollarSign, Target, Activity, Loader2, Settings as SettingsIcon, Trash2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import DashboardHome from './admin/DashboardHome';
import KanbanPipeline from './admin/KanbanPipeline';
import LeadsManagement from './admin/LeadsManagement';
import ClientsManagement from './admin/ClientsManagement';
import ProjectsManagement from './admin/ProjectsManagement';
import Settings from './admin/Settings';
import TrashBin from './admin/TrashBin';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: <LayoutDashboard size={20} />, end: true },
    { name: 'Pipeline', path: '/admin/dashboard/pipeline', icon: <KanbanSquare size={20} /> },
    { name: 'Leads', path: '/admin/dashboard/leads', icon: <Target size={20} /> },
    { name: 'Clientes', path: '/admin/dashboard/clients', icon: <Users size={20} /> },
    { name: 'Projetos', path: '/admin/dashboard/projects', icon: <Briefcase size={20} /> },
    { name: 'Lixeira', path: '/admin/dashboard/trash', icon: <Trash2 size={20} /> },
    { name: 'Configurações', path: '/admin/dashboard/settings', icon: <SettingsIcon size={20} /> },
  ];

  const isActive = (path, end) => {
    if (end) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex bg-[#080710]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0d0f17] border-r border-[#22D3EE]/10 fixed h-full flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/5">
          <Logo size={32} showText={true} />
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.name.toLowerCase()}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-sm font-medium ${
                isActive(item.path, item.end)
                  ? 'bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/30'
                  : 'text-[#94A3B8] hover:bg-white/5 hover:text-[#F8FAFC]'
              }`}
            >
              {item.icon}
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="mb-3 px-2">
            <p className="text-xs text-[#94A3B8]">Conectado como</p>
            <p className="text-sm text-[#F8FAFC] truncate" data-testid="user-email">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[#94A3B8] hover:bg-red-500/10 hover:text-red-400 transition-all text-sm"
          >
            <LogOut size={18} />
            Sair
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 ml-64 p-8">
        <Routes>
          <Route index element={<DashboardHome />} />
          <Route path="pipeline" element={<KanbanPipeline />} />
          <Route path="leads" element={<LeadsManagement />} />
          <Route path="clients" element={<ClientsManagement />} />
          <Route path="projects" element={<ProjectsManagement />} />
          <Route path="trash" element={<TrashBin />} />
          <Route path="settings" element={<Settings />} />
        </Routes>
      </main>
    </div>
  );
}
